import React, {ReactElement} from 'react';
import {NewOccurrenceReportEditorProps} from '../../../../@types/editor-props';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import {Stack} from '@mui/system';
import {uuid} from '../../../../utils/utils';
import {DesktopDateTimePicker, LocalizationProvider} from '@mui/x-date-pickers';
import moment from 'moment';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';

export default function NewOccurrenceReportEditor(props: NewOccurrenceReportEditorProps): ReactElement {
    const { occurrence } = props;
    const uuidRef = React.useRef(uuid());

    return <Box sx={{ height: '100%', p: 1 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
                id={`source-name-input-${uuidRef.current}`}
                label="Source Name"
                value={occurrence.sourceName}
                variant="outlined"
                disabled
                fullWidth
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                slotProps={{
                    input: {
                        'role': 'textbox',
                        'tabIndex': 0,
                        'aria-labelledby': `source-name-label-${uuidRef.current}`,
                        'aria-label': 'Source Name',
                    }
                }}
            />

            <TextField
                id={`unique-id-input-${uuidRef.current}`}
                label="Unique ID"
                value={occurrence.uniqueId}
                variant="outlined"
                disabled
                fullWidth
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                slotProps={{
                    input: {
                        'role': 'textbox',
                        'tabIndex': 0,
                        'aria-labelledby': `unique-id-label-${uuidRef.current}`,
                        'aria-label': 'Unique ID',
                    }
                }}
            />
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDateTimePicker
                    label='Occurrence Date/Time'
                    aria-label='occurrence date/time'
                    format='MM/DD/YYYY HH:mm:ss'
                    value={occurrence.dateTime ? moment(occurrence.dateTime) : null}
                    readOnly
                    disabled
                    sx={{ width: '100%' }}
                />
            </LocalizationProvider>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
                id={`facility-name-input-${uuidRef.current}`}
                label="Facility Name"
                value={occurrence.facilityName || ''}
                variant="outlined"
                disabled
                fullWidth
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                slotProps={{
                    input: {
                        'role': 'textbox',
                        'tabIndex': 0,
                        'aria-labelledby': `facility-name-label-${uuidRef.current}`,
                        'aria-label': 'Facility Name',
                    }
                }}
            />
            <TextField
                id={`latitude-input-${uuidRef.current}`}
                label="Latitude"
                value={occurrence.latitude || ''}
                variant="outlined"
                disabled
                fullWidth
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                slotProps={{
                    input: {
                        'role': 'textbox',
                        'tabIndex': 0,
                        'aria-labelledby': `latitude-label-${uuidRef.current}`,
                        'aria-label': 'Latitude',
                    }
                }}
            />
            <TextField
                id={`longitude-input-${uuidRef.current}`}
                label="Longitude"
                value={occurrence.longitude || ''}
                variant="outlined"
                disabled
                fullWidth
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                slotProps={{
                    input: {
                        'role': 'textbox',
                        'tabIndex': 0,
                        'aria-labelledby': `longitude-label-${uuidRef.current}`,
                        'aria-label': 'Longitude',
                    }
                }}
            />
        </Stack>
        <hr />
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="occurrence details table">
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{color: 'text.secondary'}}>Field</TableCell>
                        <TableCell align="left" sx={{color: 'text.secondary'}}>Value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {occurrence.content && typeof occurrence.content === 'string' ? (
                        <TableRow>
                            <TableCell component="th" scope="row" sx={{color: 'text.secondary'}}>
                                Content
                            </TableCell>
                            <TableCell align="left" sx={{color: 'text.secondary'}}>
                                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                    {occurrence.content}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : occurrence.content && typeof occurrence.content === 'object' ? (
                        Object.entries(occurrence.content).map(([key, value]) => (
                            <TableRow key={key}>
                                <TableCell component="th" scope="row" sx={{color: 'text.secondary'}}>
                                    {key}
                                </TableCell>
                                <TableCell align="left" sx={{color: 'text.secondary'}}>
                                    {typeof value === 'object' && value !== null ?
                                        value.constructor === Array ? Array.from(value).join(' ') :
                                            Object.keys(value as Record<string, any>).map((subKey) => (
                                                <div key={subKey}>
                                                    <Typography  variant="body2" component="div" >{subKey}</Typography>

                                                    {Object.keys((value as Record<string, any>)[subKey] as Record<string, any>).map((subSubKey) => {
                                                        const subValue = ((value as Record<string, any>)[subKey] as Record<string, any>)[subSubKey];
                                                        return (
                                                            <Box key={subSubKey} sx={{ ml: 4, display: 'flex', alignItems: 'center' }}>
                                                                <Typography variant="body2" component="div" sx={{ minWidth: '150px' }}>{subSubKey}</Typography>
                                                                <Typography variant="body2" component="div" sx={{ ml: 2 }}>
                                                                    {typeof subValue === 'object' && subValue !== null
                                                                        ? JSON.stringify(subValue, null, 2)
                                                                        : String(subValue)}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </div>
                                            )) : String(value)}
                                </TableCell>
                            </TableRow>
                        ))
                    ) : null}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>;
}